import { SECTION_META, type PageNode, type SectionType, type WebsiteDefinition } from "@baze/schema";
import { safeHref } from "./helpers";

export interface NavLink {
  label: string;
  href: string;
  /** True for the page currently being rendered (multi-page sites). */
  current?: boolean;
}

const NON_NAV: ReadonlySet<SectionType> = new Set(["navbar", "footer"]);

/**
 * Navigation is derived, never hand-written:
 * - more than one page → one link per page (slug);
 * - otherwise → sections of the page with `showInNav`, linking to `#anchor`
 *   (label: navLabel, falling back to the section type label).
 */
export function derivedNav(definition: WebsiteDefinition, page: PageNode | undefined): NavLink[] {
  if (definition.pages.length > 1) {
    const pages = [...definition.pages].sort((a, b) => (a.slug === "/" ? -1 : b.slug === "/" ? 1 : 0));
    return pages.flatMap((p) => {
      const href = safeHref(p.slug);
      return href ? [{ label: p.title, href, current: p.id === page?.id }] : [];
    });
  }
  if (!page) return [];
  return page.sections.flatMap((s) => {
    if (s.showInNav !== true || s.hidden || NON_NAV.has(s.type)) return [];
    const href = safeHref(`#${s.anchor}`);
    const label = s.navLabel?.trim() || SECTION_META[s.type]?.label || s.type;
    return href ? [{ label, href }] : [];
  });
}
